import {
  crewBookingText,
  crewCancelledText,
  crewNeedsTelling,
  crewTextNumber,
  documentSubtotal,
  documentTotal,
  paperworkHeader,
  formatCents,
  isCrewBooked,
  bookedDay,
  measuredQuantity,
  orderStatusLabel,
  payoutSummary,
  toggleChecklistStep,
  UNITS,
  workCompleteRefusal,
  workOrderStep,
  WORK_ORDER_BASE_STATUS,
  WORK_ORDER_STEPS,
  workOrderScopeFromEstimate,
  type PayoutState,
  type Product,
  type RoofLayouts,
  type Row,
  type WorkOrder,
  type WorkOrderItem,
  type Job,
  crewLabel,
} from "@roof-hawk/core";
import { useCallback, useRef, useState } from "react";

import { ErrorNote, Modal, Row as LabelRow } from "../../components/ui";
import { t, tn } from "../../i18n";
import {
  DeleteLineButton,
  EditHistoryProvider,
  UndoRedoButtons,
  useTrackedRows,
} from "../../lib/editHistory";
import { fmtDateTime, toLocalDateTimeInput } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import { printOrder } from "../../lib/printOrder";
import {
  allWorkOrdersComplete,
  deleteRow,
  fetchBoughtUpgrades,
  fetchCompanySettings,
  fetchJobForBalance,
  fetchJobForBooking,
  fetchReferenceData,
  raiseBalanceRequest,
  sendText,
  updateRow,
  fetchRoofLayouts,
} from "../../lib/queries";
import {
  BalanceRequestModal,
  type BalanceRequest,
} from "../job/BalanceRequestModal";
import { SendWorkOrderModal } from "./SendWorkOrderModal";
import { syncCrewBookingToCalendar } from "../../lib/gmail";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";
import { NumberCell, ProductSelect, TextCell } from "../estimate/EstimateCard";

/**
 * The work order editor — new in v1; the prototype only listed work orders.
 *
 * Labour is untaxed by default (BUSINESS-RULES.md), and line prices are entered
 * by hand: `crew_labor_rates` exists but is empty, so nothing can drive them yet
 * (open item A3/F3).
 */
export function WorkOrderEditor({
  orderId,
  products,
  onClose,
  onSaved,
}: {
  orderId: string;
  products: Product[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { trades, people } = useSession();
  const [error, setError] = useState<string | null>(null);
  /** What happened when the crew was told, said once, where somebody is looking. */
  const [crewNote, setCrewNote] = useState<string | null>(null);
  /**
   * The balance letter, waiting for the rep to read it.
   *
   * Jonathon, 2026-09-06: marking the last work order complete should pop up an
   * email prompt "so they can manually review the email". Held here rather than
   * sent, because the crew has just been on somebody's roof and the person who
   * was there is the one who should have the last word on what is said.
   */
  const [balance, setBalance] = useState<{
    job: Job;
    request: BalanceRequest;
  } | null>(null);
  /**
   * Whether the letter to the crew is open.
   *
   * Opened by booking the day as well as by the button, because that is the
   * moment Lysa is describing — "when scheduling WOs and MOs I can't tell if
   * anything was sent to the crews". It is a prompt, never a send: closing it
   * leaves the booking exactly as it was, and the text and the invitation have
   * gone either way.
   */
  const [sending, setSending] = useState(false);
  const [copyingScope, setCopyingScope] = useState(false);
  /**
   * The Description as last typed. The box saves on blur, and pressing Copy
   * From Estimate is that blur, so its save is still on its way when the copy
   * asks whether anything is typed; `order.description` would still say "".
   */
  const typedDescription = useRef<{ orderId: string; text: string } | null>(
    null,
  );

  const reference = useAsync(fetchReferenceData, []);
  const orderQuery = useAsync(async () => {
    const { data, error: e } = await supabase
      .from("work_orders")
      .select("*, items:work_order_items(*)")
      .eq("id", orderId)
      .maybeSingle();
    if (e) throw new Error(e.message);
    return data as WorkOrder | null;
  }, [orderId]);

  const order = orderQuery.data;

  /** Re-read the order and push the new totals back to the estimate. */
  const reload = useCallback(async () => {
    await orderQuery.reload();
    await onSaved();
  }, [orderQuery, onSaved]);

  const guard = useCallback(
    async (action: () => Promise<void>) => {
      try {
        await action();
        await reload();
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [reload],
  );

  // The job's roof, so a line added by hand can work its own quantity out.
  // Above the guard below, because hooks cannot be called conditionally.
  const layouts = useAsync(
    async () => (order ? await fetchRoofLayouts(order.job_id) : []),
    [order?.job_id],
  );

  // The address the crew drives to and the name the company signs off as. Both
  // are only read when somebody is told about a booking, but hooks cannot be
  // called conditionally.
  const bookingJob = useAsync(
    async () => (order ? await fetchJobForBooking(order.job_id) : null),
    [order?.job_id],
  );
  const settings = useAsync(fetchCompanySettings, []);

  /*
   * Telling the crew what day they have, and taking it back.
   *
   * Jonathon, 2026-09-01: "is it going to show up on crews calendar for their
   * work order scheduled? that needs to happen." A crew is not a user of Roof
   * Board and never sees the Cal tab, so until now the day reached them because
   * somebody remembered to pass it on.
   *
   * Both routes are tried, because crews are not one kind of contact: an
   * invitation for the ones who keep a calendar, a text for the ones who are a
   * name and a phone number. Neither is assumed to have worked — what happened
   * is said on screen, and a calendar refusal also lands on the row so it
   * outlives this modal.
   *
   * It runs after the save and never undoes it. A booking that reached nobody
   * is a problem; a booking lost because Google was down is a worse one.
   */
  const tellCrew = useCallback(
    async (saved: WorkOrder) => {
      const booked = isCrewBooked(saved);
      const crew = (reference.data?.crews ?? []).find(
        (c) => c.id === saved.crew_id,
      );
      const job = bookingJob.data;
      const company = settings.data?.name ?? null;
      const said: string[] = [];

      const calendar = await syncCrewBookingToCalendar({
        workOrderId: saved.id,
        booked,
      });
      if (!calendar.ok)
        said.push(
          t("It did not reach a calendar: {error}", {
            error: String(calendar.error),
          }),
        );
      else if (booked && (calendar.uninvited ?? []).length > 0)
        said.push(
          t("{who} has no email address, so no invitation was sent.", {
            who: (calendar.uninvited ?? []).join(t(" and ")),
          }),
        );

      // The text. Only when there is something new to say — a crew re-texted
      // every time somebody saves the order stops reading the texts, which
      // costs more than the one that mattered.
      const number = crewTextNumber(crew);
      const told = saved.crew_told_for ?? null;
      const day = bookedDay(saved);

      if (booked && crewNeedsTelling(saved) && job) {
        if (!number)
          said.push(
            t("{crew} has no phone number, so no text was sent.", {
              crew: crewLabel(crew) ?? t("That crew"),
            }),
          );
        else {
          const when = new Date(day!).toLocaleString([], {
            weekday: "short",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          });
          const body = crewBookingText({
            order: saved,
            job,
            companyName: company,
            when,
          });
          try {
            if (body) await sendText({ to: number, body, jobId: saved.job_id });
            // Stamped only once Twilio has taken it. A row claiming the crew
            // was told, when they were not, is how somebody stops chasing.
            await updateRow("work_orders", saved.id, {
              crew_told_at: new Date().toISOString(),
              crew_told_for: day,
            });
            said.push(
              t("{crew} was texted the day.", {
                crew: crewLabel(crew) ?? t("The crew"),
              }),
            );
          } catch (e) {
            said.push(
              t("{crew} could not be texted: {error}", {
                crew: crewLabel(crew) ?? t("The crew"),
                error: e instanceof Error ? e.message : String(e),
              }),
            );
          }
        }
      }

      // Unbooked, and they had been told. Somebody has to say so — a crew that
      // was told Tuesday and never told otherwise turns up on Tuesday.
      if (!booked && told && number && job) {
        const when = new Date(told).toLocaleString([], {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
        try {
          await sendText({
            to: number,
            body: crewCancelledText({ job, companyName: company, when }),
            jobId: saved.job_id,
          });
          await updateRow("work_orders", saved.id, { crew_told_for: null });
          said.push(
            t("{crew} was told it is off.", {
              crew: crewLabel(crew) ?? t("The crew"),
            }),
          );
        } catch (e) {
          said.push(
            t("{crew} could not be told it is off: {error}", {
              crew: crewLabel(crew) ?? t("The crew"),
              error: e instanceof Error ? e.message : String(e),
            }),
          );
        }
      }

      setCrewNote(said.length > 0 ? said.join(" ") : null);
    },
    [reference.data, bookingJob.data, settings.data],
  );

  // Only blank before the first load. Re-reading after an edit must not tear
  // the modal down and rebuild it — that is what made adding a line item look
  // like the screen refreshing.
  if (!order) return null;

  const items = (order.items ?? [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const patch = (values: Record<string, unknown>) =>
    guard(() => updateRow("work_orders", order.id, values));

  /*
   * A change that alters what the crew was told, and therefore has to reach
   * them: the tick, the day, the length of it, or who is going.
   *
   * Deliberately not every save. A note typed into the office notes is not news
   * to a crew, and a crew texted about one would stop reading the texts.
   */
  const AFFECTS_THE_CREW = ["scheduled_at", "start_at", "end_at", "crew_id"];

  const patchBooking = (values: Record<string, unknown>) =>
    guard(async () => {
      await updateRow("work_orders", order.id, values);
      if (!AFFECTS_THE_CREW.some((column) => column in values)) return;
      // Re-read rather than trusting the patch: the tick and the day are set by
      // different controls, and what the crew is told has to come from the row
      // as it now stands, not from the half of it that just changed.
      const { data } = await supabase
        .from("work_orders")
        .select("*")
        .eq("id", order.id)
        .maybeSingle();
      if (data) await tellCrew(data as WorkOrder);
    });

  /**
   * The last work order finishing is what makes the balance due.
   *
   * Deliberately quiet when there is nothing to ask for — a repair paid in full
   * at signing, a job whose balance is zero, or one somebody has already sent
   * the request for. A rep marking the second of three work orders complete
   * should see nothing at all, and a failure here must never make marking the
   * work complete look like it failed.
   */
  const offerTheBalance = async () => {
    try {
      if (!order.job_id) return;
      if (!(await allWorkOrdersComplete(order.job_id))) return;

      const job = await fetchJobForBalance(order.job_id);
      if (!job) return;

      const request = await raiseBalanceRequest(job);
      if (!request) return;

      setBalance({ job, request });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const selectedTrades = Array.isArray(order.trades)
    ? order.trades
    : order.trades
      ? [order.trades]
      : [];

  const crew = (reference.data?.crews ?? []).find(
    (c) => c.id === order.crew_id,
  );
  const crewName = crewLabel(crew);
  /** Why "Work complete" will not tick, in the screen's language. */
  const refusedAs = workCompleteRefusal(order);
  const completeRefusal = refusedAs ? t(refusedAs) : null;
  const payoutLine = describePayout(
    order,
    items,
    crew?.is_in_house === true,
    (id) => people.find((person) => person.id === id)?.full_name ?? null,
  );

  /**
   * Fill the Description from the estimate this order prices.
   *
   * It replaces rather than adds, so pressing it twice does not say the job
   * twice — and asks first when something is already typed, because that may
   * be a note for the crew the estimate never had.
   */
  const copyScope = async () => {
    const estimateId = order.estimate_id;
    if (!estimateId) return;
    setCopyingScope(true);
    try {
      const { data, error: e } = await supabase
        .from("estimate_items")
        .select(
          "description, note, section, work_doing, customer_visible, upgrade_group, sort_order",
        )
        .eq("estimate_id", estimateId);
      if (e) throw new Error(e.message);
      // The upgrades the customer ticked: signing put them on this estimate as
      // lines, and they are work.
      const bought = await fetchBoughtUpgrades(estimateId);
      const text = workOrderScopeFromEstimate(data ?? [], bought);
      if (!text) {
        setError(t("The estimate has no lines or scope to copy."));
        return;
      }
      const last = typedDescription.current;
      const typed = (
        last?.orderId === order.id ? last.text : (order.description ?? "")
      ).trim();
      if (
        typed &&
        typed !== text &&
        !window.confirm(
          t("Replace the description with what is on the estimate?") +
            "\n\n" +
            t("What is typed here now will be lost."),
        )
      )
        return;
      typedDescription.current = { orderId: order.id, text };
      await patch({ description: text });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCopyingScope(false);
    }
  };

  /** Delete the whole order, once somebody has said so out loud. */
  const deleteOrder = () => {
    if (
      !window.confirm(
        t("Delete Work Order WO-{n}?", { n: order.order_number }) +
          "\n\n" +
          t("Its lines go with it. This cannot be undone."),
      )
    )
      return;
    void (async () => {
      await deleteRow("work_orders", order.id);
      await onSaved();
      onClose();
    })();
  };

  return (
    <Modal
      title={t("Work Order WO-{n}", { n: order.order_number })}
      subtitle={t(
        "Labour for this job. Totals feed the estimate's Labor Cost line.",
      )}
      onClose={onClose}
      wide
      actions={
        <>
          {/* Red, in the action row, beside Done. Jonathon, 2026-08-27: the
              delete has to be somewhere it can be found — it was a grey line of
              text at the foot of a long editor, under everything, which is
              where you look last. It still asks first: an order deleted by
              accident takes its lines with it. */}
          <button className="btn-danger" onClick={deleteOrder}>
            {t("Delete")}
          </button>
          <button className="btn-primary" onClick={onClose}>
            {t("Done")}
          </button>
        </>
      }
    >
      {/* Everything inside can be undone. The stack belongs to this editor and
          is forgotten when it closes — see lib/editHistory.tsx. */}
      <EditHistoryProvider onChanged={reload}>
        <ErrorNote error={error} />
        {/* What reached the crew, said once, where the person who just booked
            them is looking. A calendar refusal also survives on the row. */}
        {crewNote && (
          <div className="config-warn" role="status">
            {crewNote}{" "}
            <button className="btn-ghost" onClick={() => setCrewNote(null)}>
              {t("Dismiss")}
            </button>
          </div>
        )}
        {/* The last attempt to reach a calendar, if it failed. Written by
            calendar-sync onto the work order, so it outlives the tab that
            caused it — a crew nobody knows was never told is the whole thing
            this feature exists to prevent. */}
        {order.crew_event_error && (
          <div className="config-warn" role="status">
            {/* Not "is not on a calendar": a cancel Google refused leaves the
                event on one, and the message below says which it is. */}
            <strong>
              {t("Google Calendar is out of step with this booking.")}
            </strong>{" "}
            {order.crew_event_error}{" "}
            {t("Change the day and set it back to try again.")}
          </div>
        )}
        <UndoRedoButtons />

        <OrderItemsTable
          measurements={layouts.data ?? null}
          items={items.filter((i) => !i.is_change_order)}
          products={products}
          showColor={false}
          table="work_order_items"
          newRow={() => ({
            work_order_id: order.id,
            qty: 1,
            unit: "EA",
            unit_price: 0,
            sort_order: nextSort(items),
          })}
        />

        {/* Extras the customer agreed to, kept apart from the work we priced.
          Both cost money and both arrive late; only one of them means the
          estimate was short, and the reporting depends on the difference. */}
        <ChangeOrderSection
          orderId={order.id}
          items={items}
          products={products}
        />

        <div style={{ marginTop: 16 }}>
          <div className="totrow">
            <span className="lbl">{t("Subtotal")}</span>
            <span className="num">{formatCents(documentSubtotal(order))}</span>
            <span className="num" />
            <span className="num" />
          </div>
          <div className="totrow strong">
            <span className="lbl">
              <b>{t("Total")}</b>
            </span>
            <span className="num">
              <b>{formatCents(documentTotal(order))}</b>
            </span>
            <span className="num" />
            <span className="num" />
          </div>
        </div>

        {/* Below the line items on purpose. Once a job has been
          sent, what somebody opens this to read is the work, not
          the crew and the dates — Jonathon, 2026-08-08. */}
        <div className="cprev">
          <div>
            <LabelRow label={t("Crew")}>
              <select
                value={order.crew_id ?? ""}
                onChange={(e) =>
                  void patchBooking({ crew_id: e.target.value || null })
                }
              >
                <option value="">—</option>
                {(reference.data?.crews ?? []).map((crew) => (
                  <option key={crew.id} value={crew.id}>
                    {crewLabel(crew)}
                  </option>
                ))}
              </select>
            </LabelRow>
            <LabelRow label={t("Trades")}>
              <select
                multiple
                size={4}
                style={{ minWidth: 190 }}
                value={selectedTrades}
                onChange={(e) =>
                  void patch({
                    trades: [...e.target.selectedOptions].map((o) => o.value),
                  })
                }
              >
                {trades.map((trade) => (
                  <option key={trade.id} value={trade.name}>
                    {trade.name}
                  </option>
                ))}
              </select>
              <div className="na-hint" style={{ marginTop: 4 }}>
                {t("Ctrl/Cmd-click for more than one.")}
              </div>
            </LabelRow>
            <LabelRow label={t("Field Contact")}>
              <select
                value={order.field_contact_id ?? ""}
                onChange={(e) =>
                  void patch({ field_contact_id: e.target.value || null })
                }
              >
                <option value="">—</option>
                {/* Still here, plus whoever this order already names — a select
                    whose value is missing from its options draws as "—", which
                    would read as "no field contact" on an order that has one. */}
                {people
                  .filter(
                    (person) =>
                      person.active !== false ||
                      person.id === order.field_contact_id,
                  )
                  .map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.full_name}
                    </option>
                  ))}
              </select>
            </LabelRow>
            <LabelRow label={t("Start")}>
              <input
                type="datetime-local"
                value={toLocalDateTimeInput(order.start_at)}
                onChange={(e) =>
                  void patchBooking({
                    start_at: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </LabelRow>
            <LabelRow label={t("End")}>
              <input
                type="datetime-local"
                value={toLocalDateTimeInput(order.end_at)}
                onChange={(e) =>
                  void patchBooking({
                    end_at: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </LabelRow>
            {/*
              Read, not chosen — the material order's rule (ADR 0134), applied
              to its twin. Every status a work order can hold is one of the
              rungs on the checklist, so the dropdown could do nothing the ticks
              could not except disagree with them, and an order reading
              "completed" with nothing ticked is a lie the board believes.
            */}
            <LabelRow label={t("Status")}>
              <span>{t(orderStatusLabel(order.status))}</span>
            </LabelRow>
          </div>

          <div>
            {/* "From Company" was here. It is always CopperRidge — a box
              nobody has ever changed, on a document only this company sends.
              Jonathon, 2026-08-13. */}
            <LabelRow label={t("Description")}>
              {/* What this order is, in a sentence or several. A one-line slot
                  held it until 2026-09-23 and showed about six words of it. */}
              <TextCell
                multiline
                value={order.description}
                onSave={(v) => {
                  typedDescription.current = { orderId: order.id, text: v };
                  void patch({ description: v || null });
                }}
              />
              {/* What was typed on the estimate, so nobody types it twice for
                  the crew (Jonathon, 2026-10-17). What comes across is
                  `workOrderScopeFromEstimate`'s call, never a price. */}
              {order.estimate_id && (
                <button
                  className="addrow ghost"
                  style={{ marginTop: 6 }}
                  disabled={copyingScope}
                  onClick={() => void copyScope()}
                >
                  {copyingScope ? t("Copying…") : t("Copy From Estimate")}
                </button>
              )}
            </LabelRow>
            {/*
              One list, in the order the work happens — the material order's
              shape (ADR 0134), applied to its twin so the two editors read the
              same way. Each rung says what happened and when; what reached the
              crew is said under the rung it belongs to, because whether they
              have the order is a fact about the day being booked.
            */}
            <div className="chk-sub">{t("This Order")}</div>
            {(
              [
                ["scheduled_at", t("Booked in"), t("Book the day")],
                ["completed_at", t("Work complete"), t("Mark complete")],
                ["paid_at", t("Crew paid"), t("Mark paid")],
              ] as const
            ).map(([column, label, action]) => {
              const on = Boolean(order[column]);
              // What stops this rung being ticked, said in words. Only
              // "Work complete" has one; the sentence and the rule are in
              // `workCompleteRefusal` so every caller refuses alike.
              const refusal =
                column === "completed_at" ? completeRefusal : null;
              return (
                <div key={column}>
                  <div className={`ck${on ? " on" : ""}`}>
                    <span className="dotc">
                      <svg viewBox="0 0 24 24" fill="none" strokeWidth="3">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                    <span>{label}</span>
                    {on && (
                      <span className="when">{fmtDateTime(order[column])}</span>
                    )}
                    <span
                      className={`act${refusal ? " off" : ""}`}
                      aria-disabled={refusal ? true : undefined}
                      onClick={() =>
                        void (async () => {
                          // Refused, not ignored: the reason is already on
                          // screen under the rung, so there is nothing to say
                          // here beyond doing nothing.
                          if (refusal) return;
                          await patchBooking(
                            toggleChecklistStep(
                              WORK_ORDER_STEPS,
                              order,
                              column,
                              WORK_ORDER_BASE_STATUS,
                              new Date().toISOString(),
                            ),
                          );
                          // Only on the way in, and only on the last one.
                          if (column === "completed_at" && !on) {
                            await offerTheBalance();
                          }
                          // Booking the day is the moment to send them the
                          // order. Offered, not sent: see `sending` above.
                          if (column === "scheduled_at" && !on) {
                            setSending(true);
                          }
                        })()
                      }
                    >
                      {on ? t("Undo") : action}
                    </span>
                  </div>
                  {/* Under the rung it belongs to. A booked day the crew has
                      not been sent the order for is the gap Lysa described, and
                      this is the line that shows it. */}
                  {column === "scheduled_at" &&
                    (on || order.crew_emailed_at) && (
                      <div className="na-hint" style={{ marginLeft: 26 }}>
                        {order.crew_emailed_at
                          ? t("Work order sent to {to} on {when}.", {
                              to: order.crew_emailed_to ?? t("the crew"),
                              when: fmtDateTime(order.crew_emailed_at),
                            })
                          : t("The crew has not been sent the work order.")}
                      </div>
                    )}
                  {/* Why the tick will not go on, under the rung it refuses.
                      Standing, not on click: somebody who cannot tick it needs
                      to know what to do before reaching for it. */}
                  {refusal && (
                    <div
                      className="na-hint"
                      style={{ marginLeft: 26 }}
                      data-testid="complete-refusal"
                    >
                      {refusal}
                    </div>
                  )}
                  {/* The payout, read-only, under the rung the payout board
                      ticks (ADR 0180). The board lets paid work go after
                      fourteen days; this is where it is looked up after. */}
                  {column === "paid_at" && payoutLine && (
                    <div
                      className="na-hint"
                      style={{ marginLeft: 26 }}
                      data-testid="payout-line"
                    >
                      {payoutLine}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="chk-sub">{t("Actions")}</div>
            <div className="wo-actions">
              <button className="addrow ghost" onClick={() => setSending(true)}>
                {order.crew_emailed_at
                  ? t("Send Work Order Again")
                  : t("Send Work Order To Crew")}
              </button>
              {/* Named for who holds the copy. "Print PDF" and "Print PDF No
                  Pricing" said what the button does to a file, not which of the
                  two you want in your hand. */}
              <button
                className="addrow ghost"
                onClick={() =>
                  printWorkOrder(order, items, crewName, bookingJob.data, true)
                }
              >
                {t("Print For The Crew")}
              </button>
              <button
                className="addrow ghost"
                onClick={() =>
                  printWorkOrder(order, items, crewName, bookingJob.data, false)
                }
              >
                {t("Print Without Prices")}
              </button>
            </div>
            <div className="na-hint">
              {t(
                'Print opens the print dialog — choose "Save as PDF" to keep a copy. The crew\'s copy carries what they are paid; the other does not.',
              )}
            </div>
          </div>
        </div>
      </EditHistoryProvider>

      {sending && bookingJob.data && (
        <SendWorkOrderModal
          order={order}
          items={items}
          job={bookingJob.data}
          crew={
            (reference.data?.crews ?? []).find((c) => c.id === order.crew_id) ??
            null
          }
          companyName={settings.data?.name ?? null}
          onClose={() => setSending(false)}
          onSent={async (to) => {
            await patch({
              crew_emailed_at: new Date().toISOString(),
              crew_emailed_to: to,
            });
          }}
        />
      )}

      {/* The letter, waiting to be read. Sits inside the work order rather than
          on the job screen because that is where the rep is standing when the
          last crew finishes — it is the moment they are thinking about this
          job, and the moment their memory of how it went is freshest. */}
      {balance ? (
        <BalanceRequestModal
          job={balance.job}
          request={balance.request}
          onClose={() => setBalance(null)}
          onSent={async () => {
            setBalance(null);
            await onSaved();
          }}
        />
      ) : null}
    </Modal>
  );
}

/**
 * The payout, in one line: where it is, for how much, who, when (ADR 0180).
 *
 * Null when there is nothing to say — the work is not finished, or the crew is
 * our own and on payroll, which never reaches the payout board at all.
 */
function describePayout(
  order: WorkOrder,
  items: NonNullable<WorkOrder["items"]>,
  inHouse: boolean,
  nameOf: (profileId: string) => string | null,
): string | null {
  if (inHouse) return null;
  const payout = payoutSummary({ ...order, items });
  if (payout.state === "not_ready") return null;
  const words: Record<PayoutState, string> = {
    not_ready: "",
    needs_review: t("waiting for review"),
    approved: t("approved, to pay"),
    paid: payout.offProcess ? t("paid before review") : t("paid"),
    rejected: t("sent back"),
    held: t("on hold"),
    sent: t("billed"),
  };
  const who = payout.by ? nameOf(payout.by) : null;
  const when = payout.at ? fmtDateTime(payout.at) : null;
  const said = [who ? t("by {who}", { who }) : null, when]
    .filter(Boolean)
    .join(", ");
  return (
    t("Payout: {state}, {amount}", {
      state: words[payout.state],
      amount: formatCents(payout.amount),
    }) + (said ? ` — ${said}.` : ".")
  );
}

/**
 * Print a work order. See `printOrder`.
 *
 * The homeowner and the address lead, because Lysa asked for exactly that —
 * "if we can be sure the MO and WO both reflect the homeowners name/address on
 * the PDF file" — and because a crew holding a printed order at a kerb is
 * holding it to check they are at the right house.
 */
function printWorkOrder(
  order: WorkOrder,
  items: NonNullable<WorkOrder["items"]>,
  crewName: string | null,
  job: Job | null,
  withPricing: boolean,
): void {
  printOrder({
    title: `Work Order WO-${order.order_number}`,
    header: [
      ...paperworkHeader(job),
      ["Crew", crewName],
      [
        "Trades",
        Array.isArray(order.trades) ? order.trades.join(", ") : order.trades,
      ],
      ["Start", order.start_at ? fmtDateTime(order.start_at) : null],
      ["End", order.end_at ? fmtDateTime(order.end_at) : null],
      ["Status", order.status],
      ["Description", order.description],
    ],
    items: items.map((item) => ({
      description: item.description ?? "",
      qty: item.qty,
      unit: item.unit,
      unitPrice: item.unit_price,
    })),
    total: documentTotal(order),
    withPricing,
  });
}

/**
 * Color on an order line.
 *
 * A product with a published lineup — GAF's shingles — gets a dropdown, so a
 * rep cannot order a color that is not made. Anything else stays free text:
 * most of the catalogue has no fixed color list.
 *
 * Both are kept narrow. The dropdown used to size itself to "Thunderstorm
 * Grey" and pushed the product name out of the row it belonged to; the full
 * name is on the option list and in the title, which is where it is read.
 */
function ColorCell({
  value,
  options,
  onSave,
}: {
  value: string;
  options: string[] | null;
  onSave: (value: string) => void;
}) {
  if (!options || options.length === 0) {
    return <TextCell value={value} style={{ width: 96 }} onSave={onSave} />;
  }
  return (
    <select
      className="colorcell"
      title={value || undefined}
      value={value}
      onChange={(e) => onSave(e.target.value)}
    >
      <option value="">{t("— color —")}</option>
      {/* A color already saved that is no longer offered still has to show. */}
      {value && !options.includes(value) && (
        <option value={value}>{value}</option>
      )}
      {options.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>
  );
}

/**
 * The change order lines on a work order.
 *
 * A work order is one document, so its extras live inside it rather than on a
 * second document — unlike materials, where a whole new order is raised. The
 * section stays folded away until there is something in it, because on most
 * jobs there is not, and an empty table at the bottom of every work order is
 * noise.
 */
function ChangeOrderSection({
  orderId,
  items,
  products,
}: {
  orderId: string;
  items: WorkOrderItem[];
  products: Product[];
}) {
  const changeItems = items.filter((i) => i.is_change_order);
  const [open, setOpen] = useState(changeItems.length > 0);

  return (
    <div className="cosec">
      <div className="cosec-head">
        <button
          className={`tog${open ? " on" : ""}`}
          onClick={() => setOpen((v) => !v)}
        />
        <b>{t("Change Orders")}</b>
        <span className="na-hint">
          {changeItems.length > 0
            ? tn(
                changeItems.length,
                "1 line the customer agreed to",
                "{n} lines the customer agreed to",
              )
            : t(
                "Extra work the customer agreed to — kept out of the added-after-scheduling figure",
              )}
        </span>
      </div>

      {open && (
        <OrderItemsTable
          items={changeItems}
          products={products}
          showColor={false}
          table="work_order_items"
          newRow={() => ({
            work_order_id: orderId,
            qty: 1,
            unit: "EA",
            unit_price: 0,
            is_change_order: true,
            sort_order: nextSort(items),
          })}
        />
      )}
    </div>
  );
}

/** Shared line-item table for both order editors. */
export function OrderItemsTable({
  items,
  products,
  showColor,
  showName,
  table,
  newRow,
  measurements,
}: {
  // Both spellings are optional here because this one table renders both
  // kinds of line, and each kind has only its own column: `name` on a material
  // line, `description` on a work line. `nameField` below picks which.
  items: readonly {
    id: string;
    product_id: string | null;
    name?: string | null;
    color?: string | null;
    qty: unknown;
    unit: string | null;
    description?: string | null;
    unit_price: unknown;
    calc_note?: string | null;
  }[];
  products: Product[];
  showColor: boolean;
  /**
   * A second box, beside the product, holding what this line is actually
   * called. Jonathon, 2026-09-01: "let's go back to there being a product then
   * a name on material orders. that way we can edit the name as needed in case
   * there's specific notes we want to put on each material line item."
   *
   * The two columns were merged on 2026-08-02 for saying the same thing, which
   * they did while nobody was editing either. They are not the same thing once
   * somebody wants "Dark Bronze — leave 4 sq on site for the porch" on a line:
   * the product is the catalogue entry a price, a coverage and a colour list
   * hang off, and the name is what the supplier and QuickBooks read. Typing
   * that note into the product box, which is the only way to do it with one
   * column, clears `product_id` — and with it the colour options, the measured
   * quantity, and matching by product when the supplier's invoice comes back
   * (see `reconcile.ts`).
   */
  showName?: boolean;
  table: "work_order_items" | "material_order_items";
  /** The blank line "+ Add Line Item" writes. Undoable like everything else. */
  newRow: () => Row;
  /** The job's roof, so a line added by hand can work its own quantity out. */
  /** Every roof on the job — a house and its detached garage both count. */
  measurements?: RoofLayouts | null;
}) {
  // Material orders call it `name`, work orders call it `description`. Writing
  // the wrong one is a PostgREST error, not a quiet no-op.
  const nameField = table === "work_order_items" ? "description" : "name";
  // Every write on a line goes through here so undo knows about it. An edit
  // that skips it is an edit undo silently steps over.
  const rows = useTrackedRows(table);
  return (
    <>
      <div className="ewrap" style={{ marginTop: 14 }}>
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Product")}</th>
              {showName && <th>{t("Name")}</th>}
              {showColor && <th>{t("Color")}</th>}
              <th className="qtycol">{t("Qty")}</th>
              <th>{t("Unit")}</th>
              <th>{t("Measurement")}</th>
              <th className="num">{t("Unit Price")}</th>
              <th className="num">{t("Amount")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={7 + (showColor ? 1 : 0) + (showName ? 1 : 0)}
                  className="tnone"
                >
                  {t("No line items yet.")}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                // Colour-coded by which part of the install it is — the
                // trailer, the deck, the edges, the roof itself, the chimney,
                // the ridge. A crew chief reads this in a driveway and the
                // bands are what makes it skimmable. See `workOrderStep`.
                <tr
                  key={item.id}
                  className={
                    table === "work_order_items"
                      ? `wstep ${
                          workOrderStep(item.description ?? item.name).tone
                        }`
                      : undefined
                  }
                >
                  <td>
                    <ProductSelect
                      products={products}
                      grow={false}
                      value={item.product_id}
                      width={180}
                      // Not every line is a product. A crew is paid to haul a
                      // skylight away and a supplier bills for a pallet
                      // deposit; both are lines on the order, and neither is in
                      // a catalogue of 437 roofing items. What is typed is
                      // written to the row's own name and the product is
                      // cleared, so nothing later claims a price or a coverage
                      // for it (Jonathon, 2026-08-19).
                      text={
                        (nameField === "description"
                          ? item.description
                          : item.name) ?? null
                      }
                      onText={(typed) =>
                        void rows.patch(
                          item as unknown as Row,
                          {
                            [nameField]: typed,
                            product_id: null,
                            // The note explains a product's calculation. Typed
                            // lines have no calculation to explain.
                            calc_note: null,
                          },
                          t("Typed “{typed}”", { typed }),
                        )
                      }
                      onChange={(product) => {
                        if (!product) return;
                        // A product knows what it is measured against and the
                        // job has a roof, so the quantity can be worked out
                        // rather than typed. It lands in an editable box: a
                        // suggestion, not a rule.
                        const measured = measuredQuantity(
                          product,
                          measurements,
                        );
                        void rows.patch(
                          item as unknown as Row,
                          {
                            product_id: product.id,
                            [nameField]: product.name,
                            unit: product.unit,
                            unit_price: product.default_cost ?? item.unit_price,
                            // The note explains a calculation, so it belongs to
                            // the product that was measured. Swap drip edge for
                            // a fastener and the old "Eave + Rake = ..."
                            // describes nothing — clear it rather than leave it
                            // lying.
                            calc_note: measured ? measured.basis : null,
                            ...(measured ? { qty: measured.qty } : {}),
                          },
                          t("Set {product}", { product: product.name }),
                        );
                      }}
                    />
                  </td>
                  {showName && (
                    <td>
                      {/* What the supplier and QuickBooks read. It starts as
                          the product's own name and is a person's to change
                          from there — the product behind it is untouched, so
                          the price, the colour list, the measured quantity and
                          reconciliation all still know what this line is. */}
                      <TextCell
                        value={
                          (nameField === "description"
                            ? item.description
                            : item.name) ?? ""
                        }
                        placeholder={t("Name this line…")}
                        style={{ minWidth: 180 }}
                        onSave={(v) =>
                          void rows.patch(
                            item as unknown as Row,
                            { [nameField]: v || null },
                            v
                              ? t("Named “{name}”", { name: v })
                              : t("Cleared a name"),
                          )
                        }
                      />
                    </td>
                  )}
                  {showColor && (
                    <td>
                      <ColorCell
                        value={item.color ?? ""}
                        options={
                          products.find((p) => p.id === item.product_id)
                            ?.color_options ?? null
                        }
                        onSave={(v) =>
                          void rows.patch(
                            item as unknown as Row,
                            { color: v || null },
                            v
                              ? t("Set color to {color}", { color: v })
                              : t("Cleared a color"),
                          )
                        }
                      />
                    </td>
                  )}
                  <td>
                    <NumberCell
                      value={item.qty}
                      width={70}
                      variant="plain"
                      onSave={(qty) =>
                        void rows.patch(
                          item as unknown as Row,
                          { qty },
                          t("Changed a quantity"),
                        )
                      }
                    />
                  </td>
                  <td>
                    <select
                      style={{ width: 80 }}
                      value={item.unit ?? "EA"}
                      onChange={(e) =>
                        void rows.patch(
                          item as unknown as Row,
                          { unit: e.target.value },
                          t("Changed a unit"),
                        )
                      }
                    >
                      {UNITS.map((u) => (
                        <option key={u} value={u}>
                          {t(u)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="calccell" title={item.calc_note ?? ""}>
                    {/* Internal, and read only: which number off the report
                        drove this quantity, so it can be checked. It is not on
                        the printed copy — see printOrder. */}
                    {item.calc_note || (
                      <span className="na-hint">{t("entered")}</span>
                    )}
                  </td>
                  <td>
                    <NumberCell
                      value={item.unit_price}
                      width={100}
                      onSave={(unit_price) =>
                        void rows.patch(
                          item as unknown as Row,
                          { unit_price },
                          t("Changed a price"),
                        )
                      }
                    />
                  </td>
                  <td className="num">
                    {formatCents(
                      documentSubtotal({
                        tax_rate: 0,
                        reconcile_amt: 0,
                        items: [item as never],
                      }),
                    )}
                  </td>
                  <td>
                    {/* The only ✕ on the row. The product box used to carry a
                        second one for clearing the product, which left a blank
                        line nobody wanted and read as a delete that had not
                        worked. */}
                    <DeleteLineButton
                      table={table}
                      row={item as unknown as Row}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <button className="addrow" onClick={() => void rows.add(newRow())}>
        {t("+ Add Line Item")}
      </button>
    </>
  );
}

export const nextSort = (
  rows: readonly { sort_order?: number | null }[],
): number =>
  rows.length ? Math.max(...rows.map((r) => r.sort_order ?? 0)) + 10 : 10;

export { unwrap };
